/**
 * Auth Service
 *
 * Authentication API calls
 */

import { post, get, tokenStorage, patientTokenStorage, patientPost } from '@/lib/api'
import { API_ROUTES } from '@/config/constants'
import type {
  LoginCredentials,
  AuthResponse,
  PublicUser,
} from '@/domain/user/types'

export interface LoginResponse {
  user: PublicUser
  accessToken: string
  refreshToken: string
  // 2FA status
  requires2FA: boolean       // User has 2FA enabled, needs to verify code
  requires2FASetup: boolean  // User role requires 2FA but not configured
  twoFactorEnabled: boolean  // Whether 2FA is currently enabled
}

export interface MeResponse {
  user: PublicUser
}

// Patient login types
export interface PatientLoginCredentials {
  documentType: string
  documentNumber: string
  /**
   * Fecha de nacimiento en formato AAAA-MM-DD.
   *
   * Sustituye a la contraseña, que era el propio número de documento: quien
   * conociera una cédula veía la historia clínica de esa persona.
   */
  birthDate: string
}

export interface PatientInfo {
  id: string
  firstName: string
  lastName: string
  documentType: string
  documentNumber: string
  email: string | null
}

/**
 * Una entidad donde esta persona tiene historia.
 *
 * Una misma persona puede atenderse en varias IPS, y es lo normal: se hace la
 * ecografía donde se la autorizaron y la tomografía donde había cita. El
 * portal abre la de actividad más reciente y ofrece las demás.
 */
export interface EntidadDelPaciente {
  patientId: string
  institutionId: string
  institutionName: string
  estudios: number
  ultimoEstudio: string | null
}

export interface PatientLoginResponse {
  patient: PatientInfo
  accessToken: string
  entidades: EntidadDelPaciente[]
  isPatient: true
}

/**
 * Login user (staff)
 * Note: If requires2FA is true, tokens are NOT stored - user must complete 2FA first
 */
export async function login(credentials: LoginCredentials): Promise<LoginResponse> {
  const response = await post<LoginResponse>(API_ROUTES.AUTH.LOGIN, credentials)

  // Only store tokens if 2FA verification is not required
  // When 2FA is required, tokens are returned by the complete-login endpoint
  if (!response.requires2FA && response.accessToken && response.refreshToken) {
    tokenStorage.setTokens(response.accessToken, response.refreshToken)
  }

  return response
}

/**
 * Login patient (using document number)
 * Patients use their document number as both username and password
 * Uses completely separate token storage from staff
 */
export async function patientLogin(credentials: PatientLoginCredentials): Promise<PatientLoginResponse> {
  const response = await patientPost<PatientLoginResponse>(API_ROUTES.AUTH.PATIENT_LOGIN, credentials)
  // Store patient token in SEPARATE storage (not shared with staff)
  patientTokenStorage.setAccessToken(response.accessToken)
  patientTokenStorage.setPatientInfo(response.patient)
  guardarEntidades(response.entidades ?? [])
  return response
}

/** Dónde se recuerdan las entidades de esta persona mientras dure la sesión. */
const CLAVE_ENTIDADES = 'jh_lumina_patient_entidades'

export function guardarEntidades(entidades: EntidadDelPaciente[]): void {
  localStorage.setItem(CLAVE_ENTIDADES, JSON.stringify(entidades))
}

export function entidadesDelPaciente(): EntidadDelPaciente[] {
  try {
    const guardado = localStorage.getItem(CLAVE_ENTIDADES)
    return guardado ? (JSON.parse(guardado) as EntidadDelPaciente[]) : []
  } catch {
    return []
  }
}

/**
 * Cambia el portal a otra entidad de esta misma persona.
 *
 * El servidor comprueba que la ficha de destino es la misma persona con la
 * misma credencial. Aquí solo se guarda el token nuevo: la lista de entidades
 * no cambia, porque sigue siendo la misma persona.
 */
export async function cambiarDeEntidad(patientId: string): Promise<void> {
  const respuesta = await patientPost<{ accessToken: string; patientId: string }>(
    API_ROUTES.AUTH.PATIENT_SWITCH,
    { patientId }
  )
  patientTokenStorage.setAccessToken(respuesta.accessToken)
}

/**
 * Logout patient
 * Only clears patient tokens, does not affect staff session
 */
export async function patientLogout(): Promise<void> {
  patientTokenStorage.clearTokens()
  // Las entidades son datos de la persona que se fue. Dejarlas ahí las
  // mostraría al siguiente que entre desde el mismo equipo, que es
  // exactamente el caso de un teléfono prestado o un computador de la sala.
  localStorage.removeItem(CLAVE_ENTIDADES)
}

/**
 * Check if patient is authenticated
 */
export function isPatientAuthenticated(): boolean {
  return patientTokenStorage.getAccessToken() !== null
}

/**
 * Get stored patient info
 */
export function getStoredPatientInfo(): PatientInfo | null {
  return patientTokenStorage.getPatientInfo() as PatientInfo | null
}

/**
 * Logout user
 */
export async function logout(): Promise<void> {
  const refreshToken = tokenStorage.getRefreshToken()
  try {
    await post(API_ROUTES.AUTH.LOGOUT, { refreshToken })
  } finally {
    tokenStorage.clearTokens()
  }
}

/**
 * Get current user
 */
export async function getCurrentUser(): Promise<PublicUser> {
  const response = await get<MeResponse>(API_ROUTES.AUTH.ME)
  return response.user
}

/**
 * Refresh access token
 */
export async function refreshToken(): Promise<AuthResponse> {
  const currentRefreshToken = tokenStorage.getRefreshToken()
  if (!currentRefreshToken) {
    throw new Error('No refresh token available')
  }

  const response = await post<AuthResponse>(API_ROUTES.AUTH.REFRESH, {
    refreshToken: currentRefreshToken,
  })

  tokenStorage.setTokens(response.accessToken, response.refreshToken)
  return response
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  return tokenStorage.getAccessToken() !== null
}
